import React from 'react';
import {
  employer, employees, earningsWorkbook, deductionsWorkbook,
  validationFlags, loadResults, pipelineHistory
} from '../data/mockData';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const criticalFlags = validationFlags.filter(f => f.severity === 'CRITICAL').length;
const grossTotal = earningsWorkbook
  .filter(r => r.status !== 'CRITICAL')
  .reduce((s, r) => s + r.amount, 0);
const loadedCount = loadResults.filter(r => r.earningsStatus === 'LOADED').length;

const earningsByEe = employees.map(ee => {
  const rows = earningsWorkbook.filter(r => r.employeeUniqueId === ee.employeeUniqueId);
  return {
    name: ee.firstName,
    gross: rows.reduce((s, r) => s + r.amount, 0),
  };
});

const COLORS = ['#1c3d5a','#234d72','#2d6093','#366fa8','#4080be','#5592ca'];

export default function Dashboard() {
  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Pay Period Overview</h2>
          <p>{employer.payPeriodStart} – {employer.payPeriodEnd} · {employer.companyName}</p>
        </div>
        <button className="btn btn-primary">▶ Run Pipeline</button>
      </div>

      {/* KPI Stats */}
      <div className="stats-grid">
        <div className="stat-card info">
          <div className="stat-label">Employees</div>
          <div className="stat-value">{employees.length}</div>
          <div className="stat-sub">7 W-2 · 1 Contractor</div>
        </div>
        <div className="stat-card success">
          <div className="stat-label">Gross Earnings</div>
          <div className="stat-value">${grossTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="stat-sub">{earningsWorkbook.filter(r => r.status !== 'CRITICAL').length} earning rows</div>
        </div>
        <div className="stat-card critical">
          <div className="stat-label">Critical Flags</div>
          <div className="stat-value">{criticalFlags}</div>
          <div className="stat-sub">Blocking load • Action required</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-label">Loaded / Total</div>
          <div className="stat-value">{loadedCount}/{employees.length}</div>
          <div className="stat-sub">1 blocked · 1 GUID unresolved</div>
        </div>
      </div>

      <div className="grid-2 section-gap">
        {/* Gross by Employee Chart */}
        <div className="card">
          <div className="card-header">
            <h2>Gross Earnings by Employee</h2>
          </div>
          <div className="card-body" style={{ padding: '16px 8px 8px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={earningsByEe} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9e9890' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9e9890' }} axisLine={false} tickLine={false}
                  tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
                <Tooltip
                  formatter={v => [`$${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Gross']}
                  contentStyle={{ border: '1px solid #e4e1d9', borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="gross" radius={[4, 4, 0, 0]}>
                  {earningsByEe.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline Status */}
        <div className="card">
          <div className="card-header"><h2>Current Pipeline Status</h2></div>
          <div className="card-body">
            {[
              { label: 'Step 1 — Fetch Employer Config', status: 'done', detail: '8 earning codes, 6 deductions, 4 depts' },
              { label: 'Step 2 — Employee Profiles', status: 'done', detail: '8/8 profiles fetched (1 GUID warning)' },
              { label: 'Step 3 — Extractor V3 Parse', status: 'done', detail: 'Workbook generated — 16 earnings, 11 ded.' },
              { label: 'Step 4 — XLSX → JSON Convert', status: 'done', detail: 'earnings_payload.json + deductions_payload.json' },
              { label: 'Step 5 — Validation Layer', status: 'blocked', detail: '3 CRITICAL flags • Load blocked' },
              { label: 'Step 6 — API Loader', status: 'partial', detail: '7/8 employees loaded • 1 blocked' },
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '10px 0', borderBottom: i < 5 ? '1px solid var(--border)' : 'none'
              }}>
                <span style={{
                  fontSize: 13, marginTop: 1,
                  color: s.status === 'done' ? 'var(--success)' : s.status === 'blocked' ? 'var(--error)' : 'var(--warning)'
                }}>
                  {s.status === 'done' ? '✓' : s.status === 'blocked' ? '✕' : '◑'}
                </span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{s.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{s.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Run History */}
      <div className="card">
        <div className="card-header">
          <h2>Pipeline Run History</h2>
          <span className="card-count">{pipelineHistory.length} runs</span>
        </div>
        <div className="data-table-wrap">
          <table className="data-table history-table">
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Date</th>
                <th>Pay Period</th>
                <th>Status</th>
                <th className="center">Critical Flags</th>
                <th className="center">Loaded</th>
                <th className="center">Blocked</th>
              </tr>
            </thead>
            <tbody>
              {pipelineHistory.map(r => (
                <tr key={r.runId}>
                  <td><span className="run-id">{r.runId}</span></td>
                  <td>{r.date}</td>
                  <td>{r.payPeriod}</td>
                  <td>
                    <span className={`badge badge-${r.status === 'SUCCESS' ? 'success' : r.status === 'BLOCKED' ? 'critical' : 'high'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="center">
                    {r.criticalFlags > 0
                      ? <span style={{ color: 'var(--error)', fontWeight: 700 }}>{r.criticalFlags}</span>
                      : <span style={{ color: 'var(--success)' }}>0</span>}
                  </td>
                  <td className="center" style={{ color: 'var(--success)', fontWeight: 600 }}>{r.loaded}</td>
                  <td className="center">
                    {r.blocked > 0
                      ? <span style={{ color: 'var(--error)', fontWeight: 600 }}>{r.blocked}</span>
                      : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
